/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { Avatar as AvatarV, IconPerson } from '@veneer/core';
import React, { memo, useEffect, useRef } from 'react';
import './styles.scss';
import { AvatarProps } from './types';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit component based on Veneer Avatar
 * @public
 */

const TAvatar = ({
  id = generateID('clientos-ui-toolkit-avatar'),
  userProfileImg,
  size = 'small',
  label,
  handleClick,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}: AvatarProps) => {
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (avatarRef.current) {
      const element = avatarRef.current.querySelector('.label span');
      if (element) {
        const computedSize = parseFloat(getComputedStyle(element).fontSize);

        const expectedSize = size === 'small' ? 18 : 32; // desired font size

        // Apply scaling to the class dynamically
        document.documentElement.style.setProperty(
          '--avatar-scale',
          (expectedSize / computedSize).toString()
        );
      }
    }
  }, [size]);

  return (
    <div
      ref={avatarRef}
      id={id}
      className={`clientos-ui-toolkit-avatar clientos-ui-toolkit-avatar--${size} ${label ? 'label' : ''} ${handleClick ? 'hoverable' : ''}`}
    >
      <AvatarV
        id={`${id}__avatar`}
        label={label}
        onClick={handleClick ?? undefined}
        image={userProfileImg}
        icon={!label && !userProfileImg ? <IconPerson /> : undefined}
        tabIndex={handleClick ? 0 : undefined}
        role={handleClick ? 'button' : 'image'}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      />
    </div>
  );
};
export const AvatarAnalytics = withAnalytics(TAvatar);
export default memo(TAvatar);
